import { localStorageKeys, normalizeLocalSnapshot, readLocalDataStrict, withLocalDataLock, writeLocalData } from './dataPortability';
import { stableLocalValue } from './localPurchaseIntegrity';

const copy = value => JSON.parse(JSON.stringify(value));
const snapshotOf = value => normalizeLocalSnapshot(copy(value));

// A caller must hydrate its view FROM this session, retire
// it before changing identity/scope, and render status() rather than a save's
// eventual promise result. There is deliberately no refresh-baseline or force
// overwrite API. storageDisabled is a persistence policy, NOT financial masking.
export function openLocalSnapshotSession({ space, scopeId, mode, storageDisabled = false, initialData } = {}) {
  let active = mode === 'local' && !storageDisabled && typeof space === 'string' && !!space
    && typeof scopeId === 'string' && !!scopeId;
  let phase = active ? 'blocked' : 'disabled';
  let reason = active ? 'hydration-failed' : 'not-private-local';
  let dataKey = null, backupKey = null, baseline, snapshot = null;
  let generation = 0, savedGeneration = -1;
  const tickets = new WeakMap(), receipts = new WeakMap(), atomicReceipts = new WeakMap();
  let awaitingAdoption = null;

  if (active) {
    try {
      ({ dataKey, backupKey } = localStorageKeys(space));
      const raw = window.localStorage.getItem(dataKey);
      if (raw === null) {
        const backup = window.localStorage.getItem(backupKey);
        if (backup !== null) reason = 'backup-recovery-required';
        else if (initialData === undefined) reason = 'initialization-required';
        else {
          snapshot = snapshotOf(initialData);
          baseline = null;
          phase = 'pending';
          reason = '';
        }
      } else {
        // The data and token come from the SAME strict read, not a fallback
        // reader or a token sampled just before saving an existing UI snapshot.
        const hydrated = readLocalDataStrict(space);
        snapshot = copy(hydrated.data);
        baseline = hydrated.raw;
        savedGeneration = generation;
        phase = 'saved';
        reason = '';
      }
    } catch {
      reason = 'primary-unreadable';
    }
  }

  const status = () => ({ phase, reason, generation, savedGeneration, scopeId, space, dataKey,
    dirty: snapshot !== null && (generation !== savedGeneration || phase === 'adopting') });
  const failure = (code, nextPhase) => {
    if (nextPhase && active) { phase = nextPhase; reason = code; }
    return { ok: false, reason: code };
  };
  const capture = () => {
    const ticket = Object.freeze({ generation, scopeId, dataKey });
    // snapshot is replaced, never mutated. Tickets and snapshots cannot be
    // edited by a caller or transferred between sessions with identical IDs.
    tickets.set(ticket, { generation, baseline, snapshot });
    return ticket;
  };
  const eligible = pending => active && pending && pending.generation === generation
    && pending.baseline === baseline;

  const save = async (ticket = capture()) => {
    const pending = tickets.get(ticket);
    if (!pending) return failure('foreign-ticket');
    if (!eligible(pending)) return failure('superseded');
    if (phase === 'blocked' || phase === 'conflict' || phase === 'adopting') return failure(reason || 'atomic-adoption-pending');
    try {
      return await withLocalDataLock(space, () => {
        // Every lifetime/generation/token check runs inside the existing lock,
        // before the writer can mutate either primary OR backup.
        if (!eligible(pending)) return failure('superseded');
        if (phase === 'blocked' || phase === 'conflict' || phase === 'adopting') return failure(reason || 'atomic-adoption-pending');
        if (localStorageKeys(space).dataKey !== dataKey) return failure('storage-scope-changed', 'blocked');
        if (pending.generation === savedGeneration) return { ok: true, skipped: true, generation, scopeId };
        let current;
        try {
          current = window.localStorage.getItem(dataKey);
          if (current !== pending.baseline || (current === null && window.localStorage.getItem(backupKey) !== null)) {
            return failure('snapshot-conflict', 'conflict');
          }
        } catch {
          return failure('storage-unreadable', 'blocked');
        }
        // Keep expectedRaw and the existing purchase integrity checks. This
        // writer is synchronous: no new edit can interleave with its commit.
        const result = writeLocalData(pending.snapshot, space, { expectedRaw: pending.baseline });
        if (!result.ok) {
          try {
            if (window.localStorage.getItem(dataKey) !== pending.baseline) return failure('snapshot-conflict', 'conflict');
          } catch { return failure('storage-unreadable', 'blocked'); }
          return failure('save-failed', 'error');
        }
        let committed;
        try {
          committed = readLocalDataStrict(space);
          if (stableLocalValue(committed.data) !== stableLocalValue(pending.snapshot)) {
            return failure('commit-acknowledgement-mismatch', 'blocked');
          }
        } catch {
          // A commit might already have succeeded. Never claim it saved or
          // acquire a later token and replay this snapshot automatically.
          return failure('commit-acknowledgement-unreadable', 'blocked');
        }
        const receipt = { ok: true, generation: pending.generation, scopeId, dataKey,
          raw: committed.raw, data: copy(committed.data) };
        receipts.set(receipt, { generation: pending.generation, raw: committed.raw });
        if (active) {
          baseline = committed.raw;
          savedGeneration = pending.generation;
          phase = generation === savedGeneration ? 'saved' : 'pending';
          reason = '';
          if (phase === 'saved') snapshot = copy(committed.data);
        }
        return receipt;
      });
    } catch {
      // A late refusal/error cannot relabel a newer edit, successful save, or
      // another scope. No retry is scheduled here; a caller must request one.
      if (eligible(pending) && !['blocked', 'conflict', 'adopting'].includes(phase)) return failure('lock-or-save-unavailable', 'error');
      return failure('superseded');
    }
  };

  const beginAtomic = () => {
    const pending = { generation, baseline, snapshot };
    let insideLock = false;
    const check = raw => {
      if (!insideLock || !eligible(pending) || ['blocked', 'conflict', 'adopting'].includes(phase)) {
        throw new Error('The local view changed or needs recovery. Keep your edits and reopen the original review.');
      }
      if (raw !== pending.baseline || localStorageKeys(space).dataKey !== dataKey) {
        failure('snapshot-conflict', 'conflict');
        throw new Error('Another view changed this workspace. Export your edits before reloading the saved workspace.');
      }
    };
    const prepareAdoption = (data, raw) => {
      const proof = Object.freeze({ generation: pending.generation, scopeId, dataKey, raw });
      atomicReceipts.set(proof, { ...pending, data: copy(data), raw });
      awaitingAdoption = proof;
      phase = 'adopting'; reason = '';
      return proof;
    };
    return {
      withLock: action => withLocalDataLock(space, async () => {
        insideLock = true;
        try { check(window.localStorage.getItem(dataKey)); return await action(); }
        finally { insideLock = false; }
      }),
      check,
      write(data, options = {}) {
        check(window.localStorage.getItem(dataKey));
        const result = writeLocalData(data, space, { ...options, expectedRaw: pending.baseline });
        if (!result.ok) { failure('save-failed', 'error'); return result; }
        return { ...result, localCommit: prepareAdoption(result.data, result.raw) };
      },
      recover(data, raw) {
        check(raw);
        if (stableLocalValue(snapshotOf(data)) !== stableLocalValue(pending.snapshot)) {
          throw new Error('The saved request differs from this view. Export your edits before reloading.');
        }
        return prepareAdoption(data, raw);
      },
    };
  };

  return {
    status,
    snapshot: () => snapshot === null ? null : copy(snapshot),
    capture,
    save,
    beginAtomic,
    adoptAtomic(proof) {
      const committed = atomicReceipts.get(proof);
      if (!committed || proof !== awaitingAdoption) return failure('foreign-commit');
      if (!eligible(committed) || phase !== 'adopting') return failure('committed-change-needs-recovery', 'conflict');
      baseline = committed.raw; snapshot = copy(committed.data);
      generation += 1; savedGeneration = generation;
      phase = 'saved'; reason = ''; awaitingAdoption = null;
      receipts.set(proof, { generation, raw: baseline });
      return { ok: true, data: copy(snapshot) };
    },
    replace(nextData) {
      if (!active) return failure('inactive-view');
      let next;
      try { next = snapshotOf(nextData); } catch { return failure('invalid-snapshot', phase === 'conflict' || phase === 'blocked' ? undefined : 'error'); }
      if (snapshot !== null && stableLocalValue(next) === stableLocalValue(snapshot)) return { ok: true, generation, unchanged: true };
      snapshot = next;
      generation += 1;
      if (phase === 'adopting') { phase = 'conflict'; reason = 'committed-change-needs-recovery'; }
      // Keep conflict/blocked sticky while retaining further edits for export.
      if (phase !== 'conflict' && phase !== 'blocked') { phase = 'pending'; reason = ''; }
      return { ok: true, generation };
    },
    isCurrentReceipt(receipt) {
      const accepted = receipts.get(receipt);
      return !!(active && accepted && phase === 'saved' && accepted.generation === generation && accepted.raw === baseline);
    },
    observeStorageChange(key) {
      if (!active || !dataKey || (key !== null && key !== dataKey && key !== backupKey)) return status();
      try {
        if (window.localStorage.getItem(dataKey) !== baseline
          || (baseline === null && window.localStorage.getItem(backupKey) !== null)) failure('snapshot-conflict', 'conflict');
      } catch { failure('storage-unreadable', 'blocked'); }
      // Observation can invalidate a baseline; it can NEVER adopt a new one.
      return status();
    },
    retire() { active = false; phase = 'retired'; reason = 'view-retired'; },
  };
}
